import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import UserProfileModal from "../components/UserProfileModal";
import { 
  ArrowLeft, 
  Users, 
  Shield, 
  ShieldAlert,
  ShieldCheck,
  Ban,
  CheckCircle,
  Search,
  Eye,
  BarChart3,
  Sword,
  Calendar,
  TrendingUp,
  Loader2,
  Crown,
  UserCog,
  Heart,
  Clock,
  Unlock,
  Timer
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import KofiModal from "../components/KofiModal";
import AdminBroadcastEditor from "../components/AdminBroadcastEditor";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ===================== Blocked Users Panel =====================
const BlockedUsersPanel = ({ user }) => {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [extendDurations, setExtendDurations] = useState({});

  const fetchBlockedUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/blocked-users`);
      setBlockedUsers(response.data.blocked_users || []);
    } catch {
      toast.error("Failed to load blocked users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const handleLift = async (clientId) => {
    setActionLoading(clientId);
    try {
      await axios.post(`${API_URL}/api/admin/blocked-users/${clientId}/lift`);
      toast.success("Block lifted");
      fetchBlockedUsers();
    } catch {
      toast.error("Failed to lift block");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtend = async (clientId) => {
    const mins = extendDurations[clientId] || 60;
    setActionLoading(clientId);
    try {
      await axios.post(`${API_URL}/api/admin/blocked-users/${clientId}/extend`, {
        duration_minutes: parseInt(mins),
      });
      toast.success(`Block extended by ${mins} minutes`);
      fetchBlockedUsers();
    } catch {
      toast.error("Failed to extend block");
    } finally {
      setActionLoading(null);
    }
  };

  const formatBlockTime = (isoStr) => {
    if (!isoStr) return "Indefinite";
    const d = new Date(isoStr);
    return d.toLocaleString();
  };

  const getTimeRemaining = (blockedUntil) => {
    if (!blockedUntil) return "Indefinite";
    const diff = new Date(blockedUntil) - new Date();
    if (diff <= 0) return "Expired";
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m remaining`;
    return `${mins}m remaining`;
  };

  return (
    <Card data-testid="blocked-users-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" />
            <CardTitle className="text-lg">Blocked Users</CardTitle>
            <Badge variant="destructive" className="text-xs" data-testid="blocked-count-badge">
              {blockedUsers.length}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchBlockedUsers} disabled={loading} data-testid="refresh-blocked-btn">
            <Loader2 className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <CardDescription>Manage spam-blocked and admin-blocked users</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : blockedUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-blocked-users">No blocked users</p>
        ) : (
          <ScrollArea className="h-[350px]">
            <div className="space-y-3">
              {blockedUsers.map((bu, idx) => (
                <div
                  key={bu.client_id || idx}
                  className="flex items-center justify-between p-3 rounded-lg border bg-red-950/10 border-red-900/30"
                  data-testid={`blocked-user-row-${bu.client_id || idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{bu.username}</span>
                      <Badge
                        className={`text-[10px] ${bu.type === "spam" ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400"}`}
                      >
                        {bu.type === "spam" ? "Spam Block" : "Admin Block"}
                      </Badge>
                    </div>
                    {bu.blocked_until && (
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Until: {formatBlockTime(bu.blocked_until)} ({getTimeRemaining(bu.blocked_until)})
                        </span>
                      </div>
                    )}
                    {bu.type === "admin" && (
                      <div className="flex items-center gap-1 mt-1">
                        <Timer className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Indefinite admin block</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {/* Extend duration select + button */}
                    {bu.type === "spam" && (
                      <div className="flex items-center gap-1">
                        <Select
                          value={String(extendDurations[bu.client_id] || "60")}
                          onValueChange={(val) => setExtendDurations(prev => ({ ...prev, [bu.client_id]: val }))}
                        >
                          <SelectTrigger className="w-[80px] h-8 text-xs" data-testid={`extend-select-${bu.client_id || idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30m</SelectItem>
                            <SelectItem value="60">1h</SelectItem>
                            <SelectItem value="120">2h</SelectItem>
                            <SelectItem value="720">12h</SelectItem>
                            <SelectItem value="1440">24h</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExtend(bu.client_id)}
                          disabled={actionLoading === bu.client_id}
                          className="text-orange-400 border-orange-400/50 text-xs h-8"
                          data-testid={`extend-block-btn-${bu.client_id || idx}`}
                        >
                          {actionLoading === bu.client_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Extend"}
                        </Button>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLift(bu.client_id || bu.user_id)}
                      disabled={actionLoading === (bu.client_id || bu.user_id)}
                      className="text-emerald-400 border-emerald-400/50 h-8"
                      data-testid={`lift-block-btn-${bu.client_id || idx}`}
                    >
                      {actionLoading === (bu.client_id || bu.user_id) ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Unlock className="w-3 h-3 mr-1" />
                          Lift
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

// ===================== Guest Ban Panel =====================
const GuestBanPanel = () => {
  const [guests, setGuests] = useState([]);
  const [bannedGuests, setBannedGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [banDurations, setBanDurations] = useState({});
  const [banReasons, setBanReasons] = useState({});

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/guests`);
      setGuests(response.data.guests || []);
      setBannedGuests(response.data.banned_guests || []);
    } catch {
      toast.error("Failed to load guest sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  const handleBan = async (identifier) => {
    setActionLoading(identifier);
    try {
      await axios.post(`${API_URL}/api/admin/guests/ban`, {
        identifier,
        reason: banReasons[identifier] || "Suspicious activity",
        duration_hours: parseInt(banDurations[identifier] || "24")
      });
      toast.success("Guest banned");
      fetchGuests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to ban guest");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnban = async (identifier) => {
    setActionLoading(identifier);
    try {
      await axios.delete(`${API_URL}/api/admin/guests/ban/${encodeURIComponent(identifier)}`);
      toast.success("Guest unbanned");
      fetchGuests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unban guest");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <Card data-testid="guest-ban-panel">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-orange-400" />
            Guest Session Management
          </CardTitle>
          <CardDescription className="text-xs">
            View active guest sessions and manage bans
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={fetchGuests} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
          <span className="ml-1 text-xs">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-2 pr-4">
              {guests.length === 0 && bannedGuests.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">No recent guest activity</p>
              )}
              
              {/* Stats summary */}
              <div className="flex gap-4 mb-4 p-2 bg-muted/30 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-green-400" />
                  <span className="text-muted-foreground">Active:</span>
                  <span className="font-bold">{guests.filter(g => !g.is_banned).length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  <span className="text-muted-foreground">Banned:</span>
                  <span className="font-bold">{bannedGuests.length}</span>
                </div>
              </div>

              {/* Active Guests */}
              {guests.map((guest) => {
                const id = guest.guest_id || guest.client_id || guest.identifier || "unknown";
                const alias = guest.guest_alias || id;
                const displayId = alias.startsWith("Anonymous_") ? alias : `${id.substring(0, 16)}...`;
                return (
                  <div
                    key={id}
                    className={`p-3 rounded-lg border ${
                      guest.is_banned 
                        ? "bg-red-950/20 border-red-900/50" 
                        : "bg-background/50 border-border/50"
                    }`}
                    data-testid={`guest-row-${id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Eye className="w-3 h-3 text-orange-400" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${alias.startsWith("Anonymous_") ? "text-amber-400" : "font-mono"}`}>
                            {displayId}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Last seen: {formatDate(guest.last_seen)}
                            {guest.ip_address && ` | IP: ${guest.ip_address}`}
                          </p>
                        </div>
                      </div>
                      {guest.is_banned && (
                        <Badge variant="destructive" className="text-xs">Banned</Badge>
                      )}
                    </div>

                    {!guest.is_banned ? (
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          placeholder="Reason..."
                          value={banReasons[alias] || ""}
                          onChange={(e) => setBanReasons({ ...banReasons, [alias]: e.target.value })}
                          className="h-7 text-xs flex-1"
                        />
                        <Select
                          value={String(banDurations[alias] || "24")}
                          onValueChange={(v) => setBanDurations({ ...banDurations, [alias]: v })}
                        >
                          <SelectTrigger className="w-20 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1h</SelectItem>
                            <SelectItem value="6">6h</SelectItem>
                            <SelectItem value="24">24h</SelectItem>
                            <SelectItem value="168">7d</SelectItem>
                            <SelectItem value="720">30d</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 text-xs"
                          onClick={() => handleBan(alias)}
                          disabled={actionLoading === alias}
                          data-testid={`ban-guest-${alias}`}
                        >
                          {actionLoading === alias ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                          Ban
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs mt-1"
                        onClick={() => handleUnban(alias)}
                        disabled={actionLoading === alias}
                        data-testid={`unban-guest-${alias}`}
                      >
                        {actionLoading === alias ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3 mr-1" />}
                        Unban
                      </Button>
                    )}
                  </div>
                );
              })}

              {/* Banned Guests (not in active list) */}
              {bannedGuests
                .filter(bg => !guests.some(g => (g.guest_id || g.client_id || g.identifier) === bg.identifier || g.guest_alias === bg.identifier))
                .map((bg) => {
                  const displayId = bg.guest_alias || bg.identifier;
                  const isOreAlias = displayId.startsWith("Anonymous_");
                  return (
                    <div
                      key={bg.identifier}
                      className="p-3 rounded-lg border bg-red-950/20 border-red-900/50"
                      data-testid={`banned-guest-${bg.identifier}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Ban className="w-4 h-4 text-red-400" />
                          <div>
                            <p className={`text-sm font-medium ${isOreAlias ? "text-amber-400" : "font-mono"}`}>
                              {isOreAlias ? displayId : `${displayId.substring(0, 16)}...`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Banned by {bg.banned_by_username} | Until {formatDate(bg.banned_until)}
                            </p>
                            {bg.reason && <p className="text-xs text-red-300">Reason: {bg.reason}</p>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => handleUnban(bg.identifier)}
                          disabled={actionLoading === bg.identifier}
                        >
                          {actionLoading === bg.identifier ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3 mr-1" />}
                          Unban
                        </Button>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewAsLoading, setViewAsLoading] = useState(null);
  const [showKofiModal, setShowKofiModal] = useState(false);

  // User Profile Modal (shared component)
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  const viewUserProfile = (targetUser) => {
    setProfileUserId(targetUser.id);
    setProfileModalOpen(true);
  };

  // Get token from localStorage for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
      if (error.response?.status === 403) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || (!user.is_admin && !user.is_junior_admin)) {
      navigate("/");
      return;
    }
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleUserAction = async (userId, action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/action`, {
        user_id: userId,
        action: action
      }, {
        headers: getAuthHeaders()
      });
      const messages = {
        block: "User blocked",
        unblock: "User unblocked",
        promote_admin: "User promoted to Jr Admin",
        demote_admin: "User demoted",
        grant_orestore_admin: "OreStore admin access granted",
        revoke_orestore_admin: "OreStore admin access revoked",
      };
      toast.success(messages[action] || `User ${action} successful`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const viewUserStats = async (targetUser) => {
    setSelectedUser(targetUser);
    setShowUserModal(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${targetUser.id}/stats`, {
        headers: getAuthHeaders()
      });
      setUserStats(response.data);
    } catch (error) {
      toast.error("Failed to load user stats");
    }
  };

  const viewAsUser = async (targetUser) => {
    if (!user.is_admin) {
      toast.error("Only full admins can use this feature");
      return;
    }
    
    setViewAsLoading(targetUser.id);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${targetUser.id}/view-as`, {
        headers: getAuthHeaders()
      });
      
      // Store the view-as data in sessionStorage for the dashboard to use
      sessionStorage.setItem("viewAsUser", JSON.stringify({
        user: response.data.user,
        personalTrackers: response.data.personal_trackers,
        guilds: response.data.guilds
      }));
      
      toast.success(`Now viewing as ${targetUser.username}`);
      navigate("/?viewAs=" + targetUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load user view");
    } finally {
      setViewAsLoading(null);
    }
  };

  const filteredUsers = searchQuery.trim()
    ? users.filter(u => 
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!user || (!user.is_admin && !user.is_junior_admin)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Admin Panel
              </h1>
              <p className="text-sm text-muted-foreground">
                {user.is_admin ? "Full Admin Access" : "Junior Admin Access"}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            {users.length} Users
          </Badge>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {users.filter(u => u.is_admin || u.is_junior_admin).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <Ban className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {users.filter(u => u.is_blocked).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Blocked</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Sword className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {users.reduce((sum, u) => sum + (u.total_kills || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Kills</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Server Broadcast */}
        <AdminBroadcastEditor />

        {/* OreStore Panel link (full admin / orestore admin only) */}
        {(user.is_admin || user.is_orestore_admin) && (
          <Card className="bg-gradient-to-br from-[#3a2814] to-[#1a0e05] border-2 border-amber-700/50">
            <CardContent className="py-4 flex items-center gap-4 flex-wrap">
              <img
                src="https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png"
                alt="OreStore"
                className="h-10 w-auto"
              />
              <div className="flex-1 min-w-[180px]">
                <div className="font-extrabold text-orange-200">OreStore Panel</div>
                <div className="text-xs text-orange-300/70">
                  Stock health, sales analytics with profit/margin, offers, BBCode generator, config & webhook.
                </div>
              </div>
              <Button
                onClick={() => navigate("/admin/orestore")}
                className="bg-amber-600 hover:bg-amber-500 text-white"
                data-testid="open-orestore-panel-btn"
              >
                Open OreStore Panel →
              </Button>
            </CardContent>
          </Card>
        )}

        {/* User Management */}
        <Card className="bg-card/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Management
            </CardTitle>
            <CardDescription>
              Manage users, view statistics, and control access
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {filteredUsers.map((targetUser) => (
                    <div
                      key={targetUser.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        targetUser.is_blocked 
                          ? "bg-red-950/20 border-red-900/50" 
                          : "bg-background/50 border-border/50"
                      }`}
                      data-testid={`user-row-${targetUser.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {targetUser.avatar ? (
                          <img
                            src={targetUser.avatar}
                            alt={targetUser.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {targetUser.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewUserProfile(targetUser)}
                              className="font-medium hover:text-primary hover:underline transition-colors"
                            >
                              {targetUser.username}
                            </button>
                            {targetUser.is_admin && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">
                                <Crown className="w-3 h-3 mr-1" />
                                Admin
                              </Badge>
                            )}
                            {targetUser.is_junior_admin && !targetUser.is_admin && (
                              <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Jr Admin
                              </Badge>
                            )}
                            {targetUser.is_orestore_admin && !targetUser.is_admin && (
                              <Badge className="bg-orange-500/20 text-orange-400 text-[10px]" data-testid={`badge-orestore-admin-${targetUser.id}`}>
                                <Shield className="w-3 h-3 mr-1" />
                                OreStore Admin
                              </Badge>
                            )}
                            {targetUser.is_blocked && (
                              <Badge variant="destructive" className="text-[10px]">
                                Blocked
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{targetUser.email}</p>
                          <p className="text-xs text-muted-foreground">
                            {targetUser.total_kills} kills • Last active: {formatDate(targetUser.last_active)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewUserStats(targetUser)}
                          data-testid={`view-user-${targetUser.id}`}
                          title="View Stats"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>

                        {/* View as User - Only for full admins */}
                        {user.is_admin && targetUser.id !== user.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewAsUser(targetUser)}
                            disabled={viewAsLoading === targetUser.id}
                            className="text-purple-400 hover:text-purple-300"
                            title="View as this user"
                            data-testid={`view-as-user-${targetUser.id}`}
                          >
                            {viewAsLoading === targetUser.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserCog className="w-4 h-4" />
                            )}
                          </Button>
                        )}

                        {targetUser.id !== user.id && targetUser.email?.toLowerCase() !== "ravirhiro@gmail.com" && (
                          <>
                            {targetUser.is_blocked ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUserAction(targetUser.id, "unblock")}
                                disabled={actionLoading}
                                className="text-emerald-400 border-emerald-400/50"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Unblock
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUserAction(targetUser.id, "block")}
                                disabled={actionLoading}
                                className="text-red-400 border-red-400/50"
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Block
                              </Button>
                            )}

                            {!targetUser.is_admin && (
                              targetUser.is_junior_admin ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUserAction(targetUser.id, "demote_admin")}
                                  disabled={actionLoading}
                                >
                                  <ShieldAlert className="w-4 h-4 mr-1" />
                                  Demote
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUserAction(targetUser.id, "promote_admin")}
                                  disabled={actionLoading}
                                  className="text-blue-400 border-blue-400/50"
                                  data-testid={`promote-jr-admin-${targetUser.id}`}
                                >
                                  <ShieldCheck className="w-4 h-4 mr-1" />
                                  Make Jr Admin
                                </Button>
                              )
                            )}

                            {/* OreStore admin toggle (only full admins can grant/revoke) */}
                            {user.is_admin && !targetUser.is_admin && (
                              targetUser.is_orestore_admin ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUserAction(targetUser.id, "revoke_orestore_admin")}
                                  disabled={actionLoading}
                                  className="text-orange-400 border-orange-400/50"
                                  title="Revoke OreStore admin access"
                                  data-testid={`revoke-orestore-admin-${targetUser.id}`}
                                >
                                  <Shield className="w-4 h-4 mr-1" />
                                  Remove OreStore
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUserAction(targetUser.id, "grant_orestore_admin")}
                                  disabled={actionLoading}
                                  className="text-orange-400 border-orange-400/50"
                                  title="Grant OreStore admin access"
                                  data-testid={`grant-orestore-admin-${targetUser.id}`}
                                >
                                  <Shield className="w-4 h-4 mr-1" />
                                  OreStore Admin
                                </Button>
                              )
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Blocked Users Management Panel */}
        <BlockedUsersPanel user={user} />

        {/* Guest Banning Panel */}
        <GuestBanPanel user={user} />

        {/* Ko-fi Support */}
        <Card className="bg-[#FF5E5B]/5 border-[#FF5E5B]/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-[#FF5E5B] fill-[#FF5E5B]" />
                <div>
                  <h3 className="font-semibold text-[#FF5E5B]">Support the Project</h3>
                  <p className="text-xs text-muted-foreground">Help keep the servers running!</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowKofiModal(true)}
                size="sm"
                className="bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white"
              >
                <Heart className="w-4 h-4 mr-1 fill-white" />
                Ko-fi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />

      {/* User Profile Modal (shared component with advanced stats) */}
      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
        userId={profileUserId}
      />

      {/* User Stats Modal (admin-specific with additional info) */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              User Statistics: {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>

          {userStats ? (
            <div className="space-y-4">
              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-primary/10">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{userStats.total_kills}</p>
                    <p className="text-xs text-muted-foreground">Total Kills</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-500/10">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{userStats.weekly_kills}</p>
                    <p className="text-xs text-muted-foreground">This Week</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-500/10">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{userStats.monthly_kills}</p>
                    <p className="text-xs text-muted-foreground">This Month</p>
                  </CardContent>
                </Card>
              </div>

              {/* Kills by Boss */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Kills by Boss</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <ScrollArea className="h-[150px]">
                    <div className="space-y-1">
                      {Object.entries(userStats.kills_by_boss || {}).slice(0, 15).map(([boss, count]) => (
                        <div key={boss} className="flex justify-between text-sm">
                          <span className="truncate">{boss}</span>
                          <span className="font-bold text-primary">{count}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Recent Kills */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Recent Kills</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <ScrollArea className="h-[150px]">
                    <div className="space-y-1">
                      {(userStats.recent_kills || []).slice(0, 10).map((kill, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="truncate">{kill.boss_name} (CH {kill.channel + 1})</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(kill.killed_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
